import { useState, useEffect, useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  Timestamp,
  addDoc,
  serverTimestamp,
  increment,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, app } from "../firebase";
import {
  RefreshCcw,
  CircleX,
  ListChevronsUpDown,
  ListChevronsDownUp,
} from "lucide-react";
import { fetchForms } from "../utils/forms";
import TextAnswer from "./TextAnswer";
import { getTenantIdFromHost } from "../Tenant/getTenantId";
import InfoModal from "./InfoModal";
import SurveyStatsCards from "./SurveyStatsCards";
import FilteredStatsBox from "./FilteredStatsBox";
import InviteAndResponseRow from "./InviteAndResponseRow";
import { useAuth } from "../AuthContext";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
);

const Visualization = () => {
  const tenantId = getTenantIdFromHost();
  const [forms, setForms] = useState([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState("");
  const [responses, setResponses] = useState([]);
  const [filteredResp, setFilteredResp] = useState([]);
  const [charts, setCharts] = useState([]);
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [officeFilter, setOfficeFilter] = useState("");
  const [webformFilter, setWebformFilter] = useState("");
  const [chartType, setChartType] = useState("Bar");
  const [summary, setSummary] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [horizontalLayout, setHorizontalLayout] = useState(false);
  const [employeeIds, setEmployeeIds] = useState([]);
  const [officeIds, setOfficeIds] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [showContactInfo, setShowContactInfo] = useState(false);
  const [showNoteInput, setShowNoteInput] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteChecked, setNoteChecked] = useState(false);

  const [invites, setInvites] = useState([]);
  const [inviteRows, setInviteRows] = useState([]);
  const [unAnswered, setUnAnswered] = useState([]);

  const [inviteStatusFilter, setInviteStatusFilter] = useState("");
  const [inviteTypeFilter, setInviteTypeFilter] = useState("");
  const [phoneContactFilter, setPhoneContactFilter] = useState(false);

  const [sortField, setSortField] = useState("inviteCreatedDate");
  const [sortDirection, setSortDirection] = useState("desc");

  const [showCloseModal, setShowCloseModal] = useState(false);
  const [infoTitle, setInfoTitle] = useState("");
  const [infoText, setInfoText] = useState("");
  const [btn1Text, setBtn1Text] = useState("");
  const [btn2Text, setBtn2Text] = useState("");
  const [pendingSmsRow, setPendingSmsRow] = useState(null);
  const [pendingEmailRow, setPendingEmailRow] = useState(null);
  const [sendingSmsIds, setSendingSmsIds] = useState([]);
  const [sendingEmailIds, setSendingEmailIds] = useState([]);

  const [editEmail, setEditEmail] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editCustomerPhone, setEditCustomerPhone] = useState("");

  const [reloadSurvey, setReloadSurvey] = useState(false);

  const [viewMode, setViewMode] = useState("detailed"); // "compact" | "detailed"
  const [expandedRows, setExpandedRows] = useState({});

  const [showList, setShowList] = useState(true);
  const [showVisualize, setShowVisualize] = useState(true);

  const { currentUserProfile } = useAuth();

  const role = currentUserProfile?.role;
  //const tenantId = currentUserProfile?.tenantId;
  const employeeId = currentUserProfile?.employeeId;

  const isAdmin = role === "admin";
  const isManager = role === "manager";
  const isEmployee = role === "employee";

  const canSeeSurveyStats = isAdmin || isManager;
  const canSeeSurveyInvites = isAdmin;

  const canSeeAllResults = isAdmin || isManager;
  const canEditInternalNotes = isAdmin;
  const canExport = isAdmin || isManager; // eller bara admin

  // const isDev = window.location.hostname === "localhost";
  const isDev = false;

  //const [formFilter, setFormFilter] = useState(isDev ? "hidden" : "visible");
  const [formFilter, setFormFilter] = useState("visible");

  useEffect(() => {
    const load = async () => {
      const data = await fetchForms(formFilter);
      setForms(data);
    };
    load();
  }, [formFilter]);

  useEffect(() => {
    if (!currentUserProfile) return;

    if (currentUserProfile.role === "employee") {
      setEmployeeFilter(currentUserProfile.employeeId);
    }
  }, [currentUserProfile, selectedSurveyId]);

  // Hämta alla svar när en enkät väljs
  useEffect(() => {
    const fetchResponses = async () => {
      if (!selectedSurveyId) return;

      const qRef = query(
        collection(db, "tenants", tenantId, "responses"),
        where("surveyId", "==", selectedSurveyId),
      );

      const snapshot = await getDocs(qRef);
      const all = snapshot.docs.map((doc) => doc.data());

      setResponses(all);
      //console.log(all);

      const timestamps = all
        .map((r) => r.timestamp?.toDate?.())
        .filter(Boolean);

      const sorted = [...timestamps].sort((a, b) => a - b);
      const from = sorted[0];
      const to = sorted[sorted.length - 1];

      const employeeCounts = {};
      all.forEach((r) => {
        if (!employeeCounts[r.employeeId]) {
          employeeCounts[r.employeeId] = 1;
        } else {
          employeeCounts[r.employeeId]++;
        }
      });

      const officeCounts = {};
      all.forEach((r) => {
        if (!officeCounts[r.officeId]) {
          officeCounts[r.officeId] = 1;
        } else {
          officeCounts[r.officeId]++;
        }
      });

      // 4. Filtrera ut unika employeeIds
      const ids = Array.from(
        new Set(all.map((r) => r.employeeId).filter(Boolean)),
      );
      setEmployeeIds(ids);

      const oids = Array.from(
        new Set(all.map((r) => r.officeId).filter(Boolean)),
      );
      setOfficeIds(oids);

      setSummary({
        totalResponses: all.length,
        filteredResp: 0,
        employees: employeeCounts,
        offices: officeCounts,
        fromDate: from,
        toDate: to,
      });
    };

    fetchResponses();
  }, [selectedSurveyId, tenantId, reloadSurvey]);

  useEffect(() => {
    const fetchInvites = async () => {
      if (!selectedSurveyId) {
        setInvites([]);
        return;
      }

      const qRef = query(
        collection(db, "tenants", tenantId, "surveyInvites"),
        where("surveyId", "==", selectedSurveyId),
      );

      const snapshot = await getDocs(qRef);

      const allInvites = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setInvites(allInvites);
    };
    // console.log(invites);
    fetchInvites();
  }, [selectedSurveyId, tenantId, reloadSurvey]);

  //tolka invite-data

  const isSameInviteAndResponse = (invite, response) => {
    if (invite.responseId && response.id) {
      return invite.responseId === response.id;
    }

    if (invite.token && response.inviteToken) {
      return invite.token === response.inviteToken;
    }

    if (invite.customerId && response.customerId) {
      return invite.customerId === response.customerId;
    }

    return false;
  };

  const getInviteDate = (invite) => {
    if (!invite) {
      return;
    }
    return invite.createdAt?.toDate?.() || invite.timestamp?.toDate?.();
  };

  const isInviteAnswered = (invite) => {
    if (!invite) {
      return;
    }
    return invite.status === "submitted";
  };

  const getInviteType = (matchedResponse, response) => {
    // Försök läsa från invite först

    if (response.createdBy === "public") return "web";

    return "phone";
  };

  const wantsPhoneContact = (invite) => {
    if (!invite) {
      return;
    }
    return invite?.preferPhone;
  };

  const getInviteLink = (invite) => {
    if (!invite) {
      return;
    }
    // Byt route här till din riktiga publika route
    const token = invite.id;
    return `${window.location.origin}/feedback/${token}`;
  };

  //Hjälpare

  const shortenLabel = (text, maxLength = 22) => {
  if (!text) return "";
  return text.length > maxLength ? text.slice(0, maxLength) + "…" : text;
};

  const getPercent = (count, total) => {
  if (!total || total === 0) return 0;
  return Math.round((count / total) * 100);
};

  const copyInviteLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      alert("Länken kopierad!");
    } catch (err) {
      console.error("Kunde inte kopiera:", err);
    }
  };

  const getDisplayName = (row) => {
    return row.customerName || "";
  };

  const getDisplayEmail = (row) => {
    return row.email || row.contactEmail || "";
  };

  const getDisplayPhone = (row) => {
    return row.contactPhone || "";
  };

  const getDisplayCustomerPhone = (row) => {
    return row.customerPhone || "";
  };

  const hasContactInfo = (row) => {
    return !!(
      getDisplayEmail(row) ||
      getDisplayPhone(row) ||
      getDisplayCustomerPhone(row) ||
      getDisplayName(row)
    );
  };

  // const getRowKey = (row, index) => {
  //   return (
  //     row.id ||
  //     row.responseId ||
  //     row.token ||
  //     row.customerId ||
  //     `${row.type}-${index}`
  //   );
  // };

  const getRowKey = (row) => {
    return (
      row.customerId || `${row.inviteToken}-${row.createdAt?.seconds || ""}`
    );
  };

  const generateColors = (count) => {
    const palette = [
      "#ff6384", // Knallröd
      "#36a2eb", // Knallblå
      "#ffce56", // Knallgul
      "#4bc0c0", // Turkos
      "#ff9f40", // Orange
      "#9966ff", // Lila
      "#ff6384", // Rosa
      "#00c851", // Grön
      "#ff4444", // Röd
      "#33b5e5", // Ljusblå
    ];
    // Om det behövs fler färger än i paletten, loopa om
    return Array.from({ length: count }, (_, i) => palette[i % palette.length]);
  };

  const getBarColor = (value, qType, colorArray, max, min) => {
    // Normalisera värdet till en skala 1-10
    let val = Math.round((value / max) * 10);
    //console.log(value + "    " + max + "    " + val);

    // Korrigera om maxvärde är lägre än 10
    if (max < 10) val = Math.min(10, val);

    // Radio eller checkbox, använd fördefinierad färg
    if (qType === "radio" || qType === "checkbox") {
      return colorArray[value];
    }

    // Använd färgskalans logik efter normalisering
    if (val <= 0) return "#d60024"; // röd
    if (val <= 2) return "#ff272c"; // orange
    if (val <= 4) return "#ff592c"; // orange
    if (val <= 5) return "#f9b233"; // gul
    if (val <= 7) return "#cece32"; // gulgrön
    if (val <= 9) return "#95c11f"; // ljusgrön
    return "#0ec105"; // grön
  };

  const handleShowAnswer = (id) => {
    setSelectedCustomer(id);
    const el = document.getElementById("textAnswers");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  //SMS
  const handleInfoModalBtn1 = async () => {
    if (pendingSmsRow) {
      const rowToSend = pendingSmsRow;
      setPendingSmsRow(null);
      setShowCloseModal(false);
      await sendSurveySms(rowToSend);
      return;
    }

    if (pendingEmailRow) {
      const rowToSend = pendingEmailRow;
      setPendingEmailRow(null);
      setShowCloseModal(false);
      await sendSurveyEmailFromRow(rowToSend);
      return;
    }

    setShowCloseModal(false);
  };

  const handleInfoModalBtn2 = () => {
    setPendingSmsRow(null);
    setPendingEmailRow(null);
    setShowCloseModal(false);
  };

  const sanitizeSmsText = (text) => {
    return text
      .replace(/[–—]/g, "-")
      .replace(/[‘’]/g, "'")
      .replace(/[“”]/g, '"')
      .replace(/\u00A0/g, " "); // non-breaking space
  };

  const buildSurveySmsText = (row) => {
    //const officeLabel = row.officeName || row.officeId || "Kontor";
    const senderName = "Marie Lindström";
    const companyName = row.officeId || "Arcus begravningsbyrå";

    const text = `Hej ${row.customerName},
Vi skulle vara tacksamma om du kan tänka dig att svara på några frågor om din upplevelse av samarbetet med ${row.officeId}.

Det tar cirka 2-3 minuter:
${row.inviteLink}

Vänliga hälsningar
${senderName}
på uppdrag av ${companyName}
070-2807314`;

    return sanitizeSmsText(text);
  };

  const handleSmsClick = (row) => {
    if (row.smsSentCount > 0 && row.lastSmsSentAt) {
      setInfoTitle("SMS har redan skickats");
      setInfoText(
        `Ett SMS skickades senast ${row.lastSmsSentAt.toDate().toLocaleString("sv-SE")}. Vill du skicka igen?`,
      );
      setBtn1Text("Skicka igen");
      setBtn2Text("Avbryt");
      setShowCloseModal(true);

      setPendingSmsRow(row); // spara raden till confirm
      return;
    } else {
      setInfoTitle("Skicka SMS?");
      setInfoText(`Vill du skicka SMS?`);
      setBtn1Text("Skicka");
      setBtn2Text("Avbryt");
      setShowCloseModal(true);

      setPendingSmsRow(row); // spara raden till confirm
      return;
    }

    // sendSurveySms(row);
  };

  const sendSurveySms = async (row) => {
    setSendingSmsIds((prev) => [...prev, row.id]);
    try {
      const phone = row.customerPhone || "";

      if (!phone.trim()) {
        setInfoTitle("Telefonnummer saknas");
        setInfoText("Det finns inget telefonnummer på denna rad.");
        setBtn1Text("OK");
        setBtn2Text(null);
        setShowCloseModal(true);
        return;
      }

      if (!row.inviteLink) {
        setInfoTitle("Länk saknas");
        setInfoText("Det finns ingen enkätlänk att skicka.");
        setBtn1Text("OK");
        setBtn2Text(null);
        setShowCloseModal(true);
        return;
      }

      const message = buildSurveySmsText(row);

      const functions = getFunctions(app, "us-central1");
      const sendSMS = httpsCallable(functions, "sendSMS");

      const result = await sendSMS({
        to: phone.trim(),
        message,
      });

      await addDoc(
        collection(
          db,
          "tenants",
          tenantId,
          "surveyInvites",
          row.id,
          "sentInvites",
        ),
        {
          channel: "sms",
          messageType: "survey_invite",
          status: "sent",
          sentAt: serverTimestamp(),
          sentBy: "system",
          providerMessageId: result?.data?.sid || null,
          recipient: phone.trim(),
          text: message,
          answered: !!row.answered,
        },
      );

      await updateDoc(doc(db, "tenants", tenantId, "surveyInvites", row.id), {
        smsSentCount: increment(1),
        lastSmsSentAt: serverTimestamp(),
        lastSmsText: message,
        lastSmsStatus: "sent",
      });

      setInfoTitle("SMS skickat");
      setInfoText(`SMS skickades till ${phone.trim()}.`);
      setBtn1Text("OK");
      setBtn2Text(null);
      setShowCloseModal(true);
      handleSurveyChange(row.surveyId, true);
    } catch (error) {
      console.error("Fel vid SMS:", error);

      await addDoc(
        collection(
          db,
          "tenants",
          tenantId,
          "surveyInvites",
          row.id,
          "sentInvites",
        ),
        {
          channel: "sms",
          messageType: "survey_invite",
          status: "failed",
          sentAt: serverTimestamp(),
          sentBy: "system",
          recipient: row.customerPhone || "",
          text: row.inviteLink ? buildSurveySmsText(row) : "",
          errorMessage: error?.message || "Okänt fel",
        },
      );

      await updateDoc(doc(db, "tenants", tenantId, "surveyInvites", row.id), {
        lastSmsStatus: "failed",
      });

      setInfoTitle("SMS kunde inte skickas");
      setInfoText(error?.message || "Något gick fel.");
      setBtn1Text("OK");
      setBtn2Text(null);
      setShowCloseModal(true);
    } finally {
      setSendingSmsIds((prev) => prev.filter((id) => id !== row.id));
    }
  };

  const handleEmailClick = (row) => {
    if (row.emailSentCount > 0 && row.lastEmailSentAt) {
      setInfoTitle("E-post har redan skickats");
      setInfoText(
        `E-post skickades senast ${row.lastEmailSentAt.toDate().toLocaleString("sv-SE")}. Vill du skicka igen?`,
      );
      setBtn1Text("Skicka igen");
      setBtn2Text("Avbryt");
      setShowCloseModal(true);

      setPendingEmailRow(row);
      return;
    } else {
      setInfoTitle("Skicka e-post?");
      setInfoText("Vill du skicka e-post?");
      setBtn1Text("Skicka");
      setBtn2Text("Avbryt");
      setShowCloseModal(true);

      setPendingEmailRow(row);
      return;
    }
  };

  const sendSurveyEmailFromRow = async (row) => {
    setSendingEmailIds((prev) => [...prev, row.id]);

    try {
      const email = row.email || "";

      if (!email.trim()) {
        setInfoTitle("E-postadress saknas");
        setInfoText("Det finns ingen e-postadress på denna rad.");
        setBtn1Text("OK");
        setBtn2Text(null);
        setShowCloseModal(true);
        return;
      }

      if (!row.inviteLink) {
        setInfoTitle("Länk saknas");
        setInfoText("Det finns ingen enkätlänk att skicka.");
        setBtn1Text("OK");
        setBtn2Text(null);
        setShowCloseModal(true);
        return;
      }

      const functions = getFunctions(app, "us-central1");
      const sendSurveyEmail = httpsCallable(functions, "sendSurveyEmail");

      await sendSurveyEmail({
        to: email.trim(),
        customerName: row.customerName || "",
        inviteLink: row.inviteLink,
        officeId: row.officeId || "",
        senderName: "Marie Lindström",
        phoneNumber: "070-2807314",
      });

      await addDoc(
        collection(
          db,
          "tenants",
          tenantId,
          "surveyInvites",
          row.id,
          "sentInvites",
        ),
        {
          channel: "email",
          messageType: "survey_invite",
          status: "sent",
          sentAt: serverTimestamp(),
          sentBy: "system",
          recipient: email.trim(),
          inviteLink: row.inviteLink,
          answered: !!row.answered,
        },
      );

      await updateDoc(doc(db, "tenants", tenantId, "surveyInvites", row.id), {
        emailSentCount: increment(1),
        lastEmailSentAt: serverTimestamp(),
        lastEmailStatus: "sent",
      });

      setInfoTitle("E-post skickad");
      setInfoText(`E-post skickades till ${email.trim()}.`);
      setBtn1Text("OK");
      setBtn2Text(null);
      setShowCloseModal(true);

      handleSurveyChange(row.surveyId, true);
    } catch (error) {
      console.error("Fel vid e-post:", error);

      await addDoc(
        collection(
          db,
          "tenants",
          tenantId,
          "surveyInvites",
          row.id,
          "sentInvites",
        ),
        {
          channel: "email",
          messageType: "survey_invite",
          status: "failed",
          sentAt: serverTimestamp(),
          sentBy: "system",
          recipient: row.email || "",
          inviteLink: row.inviteLink || "",
          errorMessage: error?.message || "Okänt fel",
        },
      );

      await updateDoc(doc(db, "tenants", tenantId, "surveyInvites", row.id), {
        lastEmailStatus: "failed",
      });

      setInfoTitle("E-post kunde inte skickas");
      setInfoText(error?.message || "Något gick fel.");
      setBtn1Text("OK");
      setBtn2Text(null);
      setShowCloseModal(true);
    } finally {
      setSendingEmailIds((prev) => prev.filter((id) => id !== row.id));
    }
  };

  const formatDateForInput = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDateTime = (value) => {
    if (!value) return "";

    const date = value?.toDate
      ? value.toDate()
      : value instanceof Date
        ? value
        : new Date(value);

    return date.toLocaleString("sv-SE", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const saveInternalNote = async (inviteId, noteText, checked) => {
    if (!inviteId) return;

    const inviteRef = doc(db, "tenants", tenantId, "surveyInvites", inviteId);

    try {
      await updateDoc(inviteRef, {
        internalNote: noteText,
        internalNoteChecked: checked,
        internalNoteUpdatedAt: serverTimestamp(),
        internalNoteCheckedAt: checked ? serverTimestamp() : null,
      });
    } catch (err) {
      console.error("Kunde inte spara note:", err);
    }
    setShowNoteInput(null);
    setNoteText("");
    setNoteChecked(false);
    handleSurveyChange(showNoteInput.surveyId, true);
  };

  const openNoteInput = (row) => {
    setShowNoteInput(row);
    setNoteText(row.internalNote || "");
    setNoteChecked(row.internalNoteChecked === true);
  };

  const openContactInfo = (row) => {
    setShowContactInfo(row);
    setEditEmail(getDisplayEmail(row) || "");
    setEditPhone(getDisplayPhone(row) || "");
    setEditCustomerPhone(getDisplayCustomerPhone(row) || "");
  };

  const saveContactInfo = async () => {
    if (!showContactInfo?.id) return;

    const oldEmail = getDisplayEmail(showContactInfo) || "";
    const oldPhone = getDisplayPhone(showContactInfo) || "";
    const oldCustomerPhone = getDisplayCustomerPhone(showContactInfo) || "";

    const newEmail = editEmail.trim();
    const newPhone = editPhone.trim();
    const newCustomerPhone = editCustomerPhone.trim();

    const emailChanged =
      newEmail.toLowerCase() !== oldEmail.trim().toLowerCase();

    const phoneChanged = newPhone.trim() !== oldPhone.trim();

    const customerPhoneChanged =
      newCustomerPhone.trim() !== oldCustomerPhone.trim();

    const inviteRef = doc(
      db,
      "tenants",
      tenantId,
      "surveyInvites",
      showContactInfo.id,
    );
    try {
      await updateDoc(inviteRef, {
        email: newEmail,
        phone: newPhone,
        customerPhone: newCustomerPhone,

        contactInfoCorrected:
          showContactInfo.contactInfoCorrected ||
          emailChanged ||
          phoneChanged ||
          customerPhoneChanged,

        contactInfoCorrectedAt:
          emailChanged || phoneChanged || customerPhoneChanged
            ? serverTimestamp()
            : showContactInfo.contactInfoCorrectedAt || null,

        originalEmail:
          emailChanged && !showContactInfo.originalEmail
            ? oldEmail
            : showContactInfo.originalEmail || "",

        originalPhone:
          phoneChanged && !showContactInfo.originalPhone
            ? oldPhone
            : showContactInfo.originalPhone || "",

        originalCustomerPhone:
          customerPhoneChanged && !showContactInfo.originalCustomerPhone
            ? oldCustomerPhone
            : showContactInfo.originalCustomerPhone || "",

        emailCorrected: showContactInfo.emailCorrected || emailChanged,

        phoneCorrected:
          showContactInfo.phoneCorrected ||
          phoneChanged ||
          customerPhoneChanged,
      });
    } catch (err) {
      console.error("Kunde inte spara note:", err);
    }

    setShowContactInfo(null);
    handleSurveyChange(showContactInfo.surveyId, true);
  };

  //Sortera

  const sortedRows = useMemo(() => {
    return [...inviteRows].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (aVal instanceof Date) aVal = aVal.getTime();
      if (bVal instanceof Date) bVal = bVal.getTime();

      if (aVal === undefined) return 1;
      if (bVal === undefined) return -1;

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;

      return 0;
    });
  }, [inviteRows, sortField, sortDirection]);

  useEffect(() => {
    setExpandedRows((prev) => {
      const next = { ...prev };

      sortedRows.forEach((row) => {
        const key = getRowKey(row);

        // bara sätt default om den inte redan finns
        if (!(key in next)) {
          next[key] = viewMode === "detailed";
        }
      });

      return next;
    });
  }, [sortedRows, viewMode]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Bygg upp alla grafer och textlistor
  useEffect(() => {
    if (!selectedSurveyId || responses.length === 0 || forms.length === 0)
      return;

    const selectedForm = forms.find((f) => f.id === selectedSurveyId);
    if (!selectedForm) return;

    const filteredResponses = responses.filter((r) => {
      if (employeeFilter && r.employeeId !== employeeFilter) return false;
      if (officeFilter && r.officeId !== officeFilter) return false;
      if (webformFilter && r.createdBy !== "public") return false;
      if (startDate) {
        const start = new Date(startDate + "T00:00:00");
        if (!r.timestamp || r.timestamp.toDate() < start) return false;
      }

      if (endDate) {
        const end = new Date(endDate + "T23:59:59");
        if (!r.timestamp || r.timestamp.toDate() > end) return false;
      }

      return true;
    });

    // ✅ Sätt state EN gång
    setFilteredResp(filteredResponses);

    setSummary((prev) =>
      prev ? { ...prev, filteredResp: filteredResponses.length } : prev,
    );

    const generatedCharts = selectedForm.questions.map((question, qIndex) => {
      const allAnswers = filteredResponses
        .map((r) => r.responses[qIndex])
        .filter(Boolean);

      const commentsForQuestion = filteredResponses
        .map((r) => r.comments?.[qIndex])
        .filter((text) => text && text.trim() !== "");

      // Hantera textfrågor
      if (question.type === "text") {
        return {
          questionText: question.text,
          type: "text",
          answers: allAnswers,
          comments: commentsForQuestion,
        };
      }
      // Checkbox eller andra typer
      let flatValues = [];
      if (question.type === "checkbox") {
        flatValues = allAnswers.flatMap((v) => v.split(";").filter(Boolean));
      } else if (question.type === "scale") {
        flatValues = allAnswers.map((v) => Number(v)).filter((v) => !isNaN(v));
      } else {
        flatValues = allAnswers;
      }

      let freq = {};

      // Om det är en skala, initiera med 0 för varje värde inom skalan
      if (
        question.type === "scale" &&
        question.scaleMin !== undefined &&
        question.scaleMax !== undefined
      ) {
        for (let i = question.scaleMin; i <= question.scaleMax; i++) {
          freq[i] = 0;
        }
      }

      // Räkna förekomster
      flatValues.forEach((v) => {
        if (freq[v] !== undefined) {
          freq[v]++;
        } else {
          freq[v] = 1; // fallback för säkerhets skull
        }
      });

      // Medelvärde för skala
      const average =
        question.type === "scale" && flatValues.length
          ? (flatValues.reduce((a, b) => a + b, 0) / flatValues.length).toFixed(
              1,
            )
          : null;

      // Färger per kategori (för bar chart)
      const colors =
        question.type === "scale"
          ? Object.keys(freq).map((val) =>
              getBarColor(Number(val), question.type, null, question.scaleMax),
            )
          : generateColors(Object.keys(freq).length);

      const chartData = {
        labels: Object.keys(freq),
        datasets: [
          {
            label: "Svar",
            data: Object.values(freq),
            // backgroundColor: generateColors(Object.keys(freq).length),
            backgroundColor: colors,
            borderColor: "rgba(0,0,0,0.2)",
            borderWidth: 1,
          },
        ],
      };
      return {
        questionText: question.text,
        type: "chart",
        questionType: question.type,
        chartData,
        average,
        count: flatValues.length,
        comments: commentsForQuestion,
      };
    });

    setCharts(generatedCharts);
  }, [
    responses,
    selectedSurveyId,
    forms,
    employeeFilter,
    officeFilter,
    startDate,
    endDate,
    webformFilter,
  ]);

  //Bygg en sammanslagen lista med invites + ev response
  useEffect(() => {
    if (!selectedSurveyId) {
      setInviteRows([]);
      return;
    }

    // 1. Gör response-rader först
    const respRows = responses.map((response) => {
      const matchedInvite =
        invites.find((invite) => isSameInviteAndResponse(invite, response)) ||
        null;

      return {
        ...response,
        type: "resp",
        answeredDate: response.timestamp?.toDate?.() || null,
        inviteCreatedDate: getInviteDate(matchedInvite),
        answered: true,
        matchedInvite,
        inviteType: getInviteType(matchedInvite, response),
        preferPhone: wantsPhoneContact(matchedInvite, response),
        inviteLink: matchedInvite ? getInviteLink(matchedInvite) : null,
        smsSentCount: matchedInvite?.smsSentCount || 0,
        emailSentCount: matchedInvite?.emailSentCount || 0,
        lastSmsSentAt: matchedInvite?.lastSmsSentAt || null,
        lastEmailSentAt: matchedInvite?.lastEmailSentAt || null,
        contactInfoCorrected: matchedInvite?.contactInfoCorrected || null,
        internalNote: matchedInvite?.internalNote || null,
        internalNoteChecked: matchedInvite?.internalNote || null,
      };
    });

    // 2. Lägg bara in invites som INTE redan har ett response
    const invRows = invites
      .filter((invite) => {
        const hasMatchingResponse = responses.some((response) =>
          isSameInviteAndResponse(invite, response),
        );

        return !hasMatchingResponse;
      })
      .map((invite) => ({
        ...invite,
        type: "inv",
        inviteCreatedDate: getInviteDate(invite),
        answered: isInviteAnswered(invite),
        matchedResponse: null,
        inviteType: "web",
        preferPhone: wantsPhoneContact(invite, null),
        inviteLink: getInviteLink(invite),
      }));

    setUnAnswered(invRows);

    let filtered = [...respRows, ...invRows];

    if (inviteStatusFilter === "answered") {
      filtered = filtered.filter((r) => r.answered);
    } else if (inviteStatusFilter === "unanswered") {
      filtered = filtered.filter((r) => !r.answered);
    }

    if (inviteTypeFilter === "web") {
      filtered = filtered.filter((r) => r.inviteType === "web");
    } else if (inviteTypeFilter === "phone") {
      filtered = filtered.filter((r) => r.inviteType === "phone");
    }

    if (phoneContactFilter) {
      filtered = filtered.filter((r) => r.preferPhone);
    }

    filtered.sort((a, b) => {
      const aTime = a.inviteCreatedDate ? a.inviteCreatedDate.getTime() : 0;
      const bTime = b.inviteCreatedDate ? b.inviteCreatedDate.getTime() : 0;
      return bTime - aTime;
    });

    setInviteRows(filtered);
  }, [
    invites,
    responses,
    selectedSurveyId,
    inviteStatusFilter,
    inviteTypeFilter,
    phoneContactFilter,
  ]);

  const getResponseByCustomer = (customerId) => {
    return responses.find((r) => r.customerId === customerId);
  };

  const chartOptions = () => ({
    indexAxis: horizontalLayout ? "y" : "x",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Döljer autogenererad legend
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: { stepSize: 1 },
      },
      y: {
        beginAtZero: true,
        ticks: { stepSize: 1 },
        reverse: horizontalLayout,
      },
    },
  });

const CustomLegend = ({
  freq,
  questionType,
  colorArray,
  maxValue,
  totalAnswers,
}) => {
  if (!freq || Object.keys(freq).length === 0) return null;

  return (
    <div className="legend-container">
      <h4>Legend:</h4>

      <ul style={{ listStyleType: "none", padding: 0 }}>
        {Object.keys(freq).map((key, index) => (
          <li
            key={index}
            style={{
              marginBottom: "0.2em",
              display: "flex",
              alignItems: "flex-start",
              marginTop: questionType === "scale" ? 0 : "7px",
            }}
          >
            <div
              style={{
                width: "20px",
                height: "20px",
                minWidth: "20px",
                backgroundColor: getBarColor(
                  Number(index),
                  questionType,
                  colorArray,
                  maxValue,
                ),
                marginRight: "0.5em",
                marginTop: questionType === "scale" ? 0 : "3px",
              }}
            />

            <span style={{ lineHeight: 1.25 }}>
              {questionType === "scale" ? `Betyg ${key}` : `${key}`}
              {questionType === "scale" ? null : <br />}

              {freq[key] > 0 ? (
                <>
                  {questionType === "scale" ? " - " : null}
                  <span className="boldText">{freq[key]}</span> svar{" "}
                  <span className="boldText">
                    ({getPercent(freq[key], totalAnswers)}%)
                  </span>
                </>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

  const resetView = () => {
    setResponses([]);
    setCharts([]);
    setEmployeeFilter("");
    setOfficeFilter("");
    setWebformFilter("");
    setStartDate("");
    setEndDate("");
    setFilteredResp([]);
    setSummary(null);
    setInvites([]);
    setInviteRows([]);
    setInviteStatusFilter("");
    setInviteTypeFilter("");
    setPhoneContactFilter(false);
    setNoteText("");
  };

  const handleSurveyChange = (id, reload) => {
    if (reload) {
      setReloadSurvey((prev) => !prev);
    }

    setSelectedSurveyId(id);
    if (!id || id !== selectedSurveyId) {
      resetView();
    }

    // requestAnimationFrame(() => {
    //   document
    //     .getElementById("listsTop")
    //     ?.scrollIntoView({ behavior: "smooth" });
    // });
  };

  const openAllRows = () => {
    const next = {};
    sortedRows.forEach((row, index) => {
      next[getRowKey(row)] = true;
    });
    setExpandedRows(next);
  };

  const closeAllRows = () => {
    const next = {};
    sortedRows.forEach((row, index) => {
      next[getRowKey(row)] = false;
    });
    setExpandedRows(next);
  };

  return (
    <>
      <div className="visualizeWrapper" style={{ marginTop: 50 }}>
        <div className="surveyPicker">
          <div className="horizontalWrapper">
            <h3>
              <label>Välj enkät:</label>{" "}
            </h3>
            {selectedSurveyId ? (
              <>
                <button
                  type="button"
                  className="plainButton"
                  onClick={() => handleSurveyChange("")}
                >
                  <CircleX size={36} strokeWidth={2} color="#333" />
                </button>
              </>
            ) : null}
          </div>
          <div className="surveyPills">
            {forms.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`surveyPill ${selectedSurveyId === f.id ? "active" : ""}`}
                onClick={() => handleSurveyChange(f.id)}
              >
                {f.formName}
              </button>
            ))}

            {selectedSurveyId ? (
              <>
                <button
                  type="button"
                  className={"iconBtnL"}
                  onClick={() => handleSurveyChange(selectedSurveyId, true)}
                >
                  <RefreshCcw size={36} strokeWidth={2} color="#333" />
                </button>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {selectedSurveyId && (
        <>
          {canSeeSurveyStats && (
            <div
              id="listsTop"
              className="visualizeWrapper"
              style={{ marginTop: 50 }}
            >
              <SurveyStatsCards
                invites={invites}
                responses={responses}
                unAnswered={unAnswered}
              />
            </div>
          )}

          {canSeeSurveyInvites && (
            <div
              className="visualizeWrapper fadeSlideOpen"
              style={{ marginTop: 50 }}
            >
              <div className="wrapperHeader">
                <h2>Lista med enkätsvar</h2>

                <button
                  className="toggleBtnXL"
                  onClick={() => setShowList((prev) => !prev)}
                >
                  {showList ? (
                    <ListChevronsDownUp
                      size={36}
                      strokeWidth={2}
                      color="#333"
                    />
                  ) : (
                    <ListChevronsUpDown 
                    size={36} 
                    strokeWidth={2} 
                    color="#333"
                    />
                  )}
                </button>
              </div>
              {showList && (
                <>
                  <div
                    style={{
                      display: "flex",
                      gap: "1em",
                      flexWrap: "wrap",
                      marginBottom: "1.2em",
                      padding: "1em 0",
                      background: "#f7f7f7",
                      borderRadius: 10,
                      border: "1px solid #ddd",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ scrollMarginTop: "60px" }} />
                    <div>
                      <label>Status:</label>
                      <select
                        value={inviteStatusFilter}
                        onChange={(e) => setInviteStatusFilter(e.target.value)}
                      >
                        <option value="">Alla</option>
                        <option value="answered">Besvarade</option>
                        <option value="unanswered">Ej besvarade</option>
                      </select>
                    </div>

                    <div>
                      <label>Typ:</label>
                      <select
                        value={inviteTypeFilter}
                        onChange={(e) => setInviteTypeFilter(e.target.value)}
                      >
                        <option value="">Alla</option>
                        <option value="web">Webbenkät</option>
                        <option value="phone">Telefonintervju</option>
                      </select>
                    </div>

                    <div style={{ display: "flex", alignItems: "end" }}>
                      <label>
                        <input
                          type="checkbox"
                          checked={phoneContactFilter}
                          onChange={(e) =>
                            setPhoneContactFilter(e.target.checked)
                          }
                        />
                        Visa bara de som vill bli kontaktade per telefon
                      </label>
                    </div>
                  </div>

                  <div className="listViewToolbar">
                    {/* <button onClick={() => setViewMode("compact")}>
                  Kompakt vy
                </button>
                <button onClick={() => setViewMode("detailed")}>
                  Detaljerad vy
                </button> */}
                    <button onClick={openAllRows}>Öppna alla</button>
                    <button onClick={closeAllRows}>Stäng alla</button>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      fontSize: ".8em",
                      color: "#333",
                      gap: "1.2em",
                      fontWeight: "bold",
                      marginBottom: "0.8em",
                      marginLeft: 10,
                      cursor: "pointer",
                    }}
                  >
                    <span onClick={() => handleSort("customerId")}>Kund</span>
                    <span onClick={() => handleSort("inviteCreatedDate")}>
                      Skapad
                    </span>
                    <span onClick={() => handleSort("answeredDate")}>
                      Svarad
                    </span>
                    <span onClick={() => handleSort("employeeId")}>
                      Handläggare
                    </span>
                    <span onClick={() => handleSort("officeId")}>Kontor</span>
                    <span onClick={() => handleSort("inviteType")}>Typ</span>
                    <span onClick={() => handleSort("answered")}>Status</span>
                  </div>

                  {/* Raderna InviteAndResponseRow.js */}

                  {sortedRows.length > 0 ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.6em",
                      }}
                    >
                      {sortedRows.map((row) => {
                        const rowKey = getRowKey(row);
                        const isExpanded =
                          expandedRows[rowKey] ?? viewMode === "detailed";

                        return (
                          <InviteAndResponseRow
                            key={rowKey}
                            row={row}
                            isExpanded={isExpanded}
                            onToggleExpand={() =>
                              setExpandedRows((prev) => ({
                                ...prev,
                                [rowKey]: !isExpanded,
                              }))
                            }
                            onShowResponse={handleShowAnswer}
                            onContactInfo={openContactInfo}
                            onCopyLink={copyInviteLink}
                            handleSmsClick={handleSmsClick}
                            handleEmailClick={handleEmailClick}
                            sendingSmsIds={sendingSmsIds}
                            sendingEmailIds={sendingEmailIds}
                            formatDateTime={formatDateTime}
                            hasContactInfo={hasContactInfo}
                            getDisplayName={getDisplayName}
                            onNoteInput={openNoteInput}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <p>Inga poster hittades.</p>
                  )}

                  {/* {showContactInfo && (
                  <InfoModal
                    btn2="Stäng"
                    onConfirm={() => setShowContactInfo(null)}
                  >
                    <h3 style={{ marginBottom: "0.5em" }}>
                      {getDisplayName(showContactInfo)}
                    </h3>

                    {getDisplayEmail(showContactInfo) ? (
                      <p style={{ margin: "0.3em 0" }}>
                        <strong>E-post:</strong>{" "}
                        {getDisplayEmail(showContactInfo)}
                      </p>
                    ) : null}

                    {getDisplayPhone(showContactInfo) ? (
                      <p style={{ margin: "0.3em 0" }}>
                        <strong>Telefon (från kund):</strong>{" "}
                        {getDisplayPhone(showContactInfo)}
                      </p>
                    ) : null}

                    {getDisplayCustomerPhone(showContactInfo) ? (
                      <p style={{ margin: "0.3em 0" }}>
                        <strong>Telefon (från register):</strong>{" "}
                        {getDisplayCustomerPhone(showContactInfo)}
                      </p>
                    ) : null}

                    {!getDisplayEmail(showContactInfo) &&
                    !getDisplayPhone(showContactInfo) ? (
                      <p>Inga kontaktuppgifter angivna.</p>
                    ) : null}
                  </InfoModal>
                )} */}

                  {showContactInfo && (
                    <InfoModal
                      btn2="Stäng"
                      onConfirm={() => setShowContactInfo(null)}
                    >
                      <h3 style={{ marginBottom: "0.5em" }}>
                        {getDisplayName(showContactInfo)}
                      </h3>
                      {getDisplayPhone(showContactInfo) ? (
                        <p style={{ margin: "1.3em 0" }}>
                          <strong>Telefon (från kund):</strong>{" "}
                          {getDisplayPhone(showContactInfo)}
                        </p>
                      ) : null}
                      <hr />

                      {showContactInfo.answered ? (
                        <>
                          {getDisplayCustomerPhone(showContactInfo) ? (
                            <p style={{ margin: "0.3em 0" }}>
                              <strong>Telefon (från register):</strong>{" "}
                              {getDisplayCustomerPhone(showContactInfo)}
                            </p>
                          ) : null}

                          {getDisplayEmail(showContactInfo) ? (
                            <p style={{ margin: "0.3em 0" }}>
                              <strong>E-post:</strong>{" "}
                              {getDisplayEmail(showContactInfo)}
                            </p>
                          ) : null}
                        </>
                      ) : (
                        <div className="contactEditBox">
                          <label>
                            E-post
                            <input
                              type="email"
                              value={editEmail}
                              onChange={(e) => setEditEmail(e.target.value)}
                              placeholder="namn@example.com"
                              disabled={showContactInfo.answered}
                            />
                          </label>

                          <label>
                            Telefon (från register)
                            <input
                              type="tel"
                              value={editCustomerPhone}
                              onChange={(e) =>
                                setEditCustomerPhone(e.target.value)
                              }
                              placeholder="+467..."
                              disabled={showContactInfo.answered}
                            />
                          </label>

                          {showContactInfo.contactInfoCorrected && (
                            <p className="contactCorrectedInfo">
                              Kontaktuppgifter har ändrats manuellt.
                            </p>
                          )}

                          <button onClick={saveContactInfo}>
                            Spara kontaktuppgifter
                          </button>
                        </div>
                      )}
                    </InfoModal>
                  )}

                  {showNoteInput && (
                    <InfoModal
                      btn2="Stäng"
                      onConfirm={() => {
                        setShowNoteInput(null);
                        setNoteText("");
                        setNoteChecked(false);
                      }}
                    >
                      <div className="internalNoteBox">
                        <label
                          style={{
                            color: "rgb(49 45 41)",
                            fontWeight: "bold",
                            marginBottom: 20,
                          }}
                        >
                          Intern anteckning
                        </label>
                        {showNoteInput.answered ? (
                          <p>{showNoteInput.internalNote}</p>
                        ) : (
                          <>
                            {" "}
                            <textarea
                              rows="5"
                              value={noteText}
                              onChange={(e) => setNoteText(e.target.value)}
                              placeholder="Ex. E-post studsade, ring kund istället..."
                            />
                            <label className="internalNoteChecked">
                              <input
                                type="checkbox"
                                checked={
                                  showNoteInput.answered
                                    ? showNoteInput.internalNoteChecked
                                    : noteChecked
                                }
                                onChange={(e) =>
                                  setNoteChecked(e.target.checked)
                                }
                                disabled={showNoteInput.answered}
                              />
                              Avklarad
                            </label>
                          </>
                        )}
                        {showNoteInput.answered &&
                          showNoteInput.contactInfoCorrected && (
                            <p className="contactCorrectedInfo">
                              Kontaktuppgifter har ändrats manuellt.
                            </p>
                          )}
                        {!showNoteInput.answered && (
                          <button
                            onClick={() =>
                              saveInternalNote(
                                showNoteInput.id,
                                noteText,
                                noteChecked,
                              )
                            }
                          >
                            Spara anteckning
                          </button>
                        )}
                      </div>
                    </InfoModal>
                  )}

                  {showCloseModal && (
                    <InfoModal
                      onCancel={() => handleInfoModalBtn2()}
                      onConfirm={() => {
                        handleInfoModalBtn1();
                      }}
                      btn2={btn1Text} //avbryt
                      btn1={btn2Text} //igen
                    >
                      <h3>{infoTitle}</h3>
                      <p>{infoText}</p>
                    </InfoModal>
                  )}
                </>
              )}
            </div>
          )}

          <div
            className="visualizeWrapper fadeSlideOpen"
            style={{ marginTop: 50 }}
          >
            <div className="wrapperHeader">
              <h2>Visualisering av enkätsvar</h2>

              <button
                className="toggleBtnXL"
                onClick={() => setShowVisualize((prev) => !prev)}
              >
                {showVisualize ? (<ListChevronsDownUp
                      size={36}
                      strokeWidth={2}
                      color="#333"
                    />
                  ) : (
                    <ListChevronsUpDown 
                    size={36} 
                    strokeWidth={2} 
                    color="#333"
                    />)}
              </button>
            </div>

            {showVisualize && (
              <>
                <div className="filterWrapper">
                  {selectedSurveyId && (
                    <div
                      style={{
                        display: "flex",
                        gap: "1em",
                        flexWrap: "wrap",
                        marginBottom: "1em",
                        flexDirection: "column",
                        justifyContent: " space-between",
                        width: "98%",
                      }}
                    >
                      <div
                        id="resultsTop"
                        style={{ scrollMarginTop: "60px" }}
                      />
                      {!isEmployee && (
                        <>
                          <div style={{ marginTop: 10 }}>
                            <label>
                              <input
                                type="checkbox"
                                checked={webformFilter}
                                onChange={(e) =>
                                  setWebformFilter(e.target.checked)
                                }
                              />
                              Visa bara webbformulär
                            </label>
                          </div>

                          <div className="dateFilterWrapper">
                            <div>
                              <label>Filtrera på anställd:</label>

                              <select
                                value={employeeFilter}
                                onChange={(e) =>
                                  setEmployeeFilter(e.target.value)
                                }
                              >
                                <option value="">
                                  Alla {"("}
                                  {employeeIds && employeeIds.length
                                    ? employeeIds.length
                                    : 0}
                                  {")"}
                                </option>
                                {employeeIds.map((id) => (
                                  <option key={id} value={id}>
                                    {id}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label>Filtrera på kontor:</label>

                              <select
                                value={officeFilter}
                                onChange={(e) =>
                                  setOfficeFilter(e.target.value)
                                }
                              >
                                <option value="">
                                  Alla {"("}
                                  {officeIds && officeIds.length
                                    ? officeIds.length
                                    : 0}
                                  {")"}
                                </option>
                                {officeIds.map((id) => (
                                  <option key={id} value={id}>
                                    {id}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </>
                      )}
                      <div className="dateFilterWrapper">
                        <div>
                          <label>Från datum:</label>
                          <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="halfInput"
                          />
                        </div>
                        <div>
                          <label>Till datum:</label>
                          <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="halfInput"
                          />
                        </div>
                      </div>
                      <hr />
                      <div className="dateFilterWrapper">
                        <div>
                          <label>Diagramtyp:</label>

                          <select
                            value={chartType}
                            onChange={(e) => setChartType(e.target.value)}
                          >
                            <option value="Bar">Staplar</option>
                            <option value="Line">Linjediagram</option>
                            <option value="Doughnut">Cirkeldiagram</option>
                          </select>
                        </div>
                        <div>
                          <label>Diagramlayout:</label>

                          <label>
                            <input
                              type="radio"
                              name="layout"
                              value="vertical"
                              checked={!horizontalLayout}
                              onChange={() => setHorizontalLayout(false)}
                            />
                            Stående staplar
                          </label>

                          <label>
                            <input
                              type="radio"
                              name="layout"
                              value="horizontal"
                              checked={horizontalLayout}
                              onChange={() => setHorizontalLayout(true)}
                            />
                            Liggande staplar
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {selectedSurveyId && summary && (
                  <FilteredStatsBox
                    summary={summary}
                    filteredResp={filteredResp}
                    invites={invites}
                    responses={responses}
                    unAnswered={unAnswered}
                    isEmployee={isEmployee}
                    currentEmployeeId={currentUserProfile.employeeId}
                  />
                )}
                {charts.length > 0 ? (
                  <div style={{ marginTop: "2em" }}>
                    {charts.map((c, i) => (
                      <div key={i} className="questionViewWrapper">
                        <h4>{c.questionText}</h4>
                        {c.average && (
                          <div className="averageInfoWrapper">
                            <p>
                              Totalt antal svar: {c.count} | Medelvärde:{" "}
                              <strong>{c.average}</strong>
                            </p>
                          </div>
                        )}
                        {c.type === "chart" ? (
                          chartType === "Line" ? (
                            <div className="chart-container">
                              <Line
                                data={c.chartData}
                                options={chartOptions()}
                              />
                            </div>
                          ) : chartType === "Doughnut" ? (
                            <div className="doughnut-container">
                              <Doughnut data={c.chartData} />
                            </div>
                          ) : (
                            <div className="chart-container">
                              <Bar
                                data={c.chartData}
                                options={chartOptions()}
                              />
                            </div>
                          )
                        ) : c.answers.length > 0 ? (
                          <div className="textAnswers">
                            {c.answers.map((a, j) => (
                              <TextAnswer key={j} text={a} index={j} />
                            ))}
                          </div>
                        ) : (
                          <p>
                            <em>Inga textsvar ännu.</em>
                          </p>
                        )}
                        {/* Lägg till vår egen legend */}
                        {c.chartData && Array.isArray(c.chartData.labels) && (
                          <CustomLegend
                            freq={c.chartData.labels.reduce(
                              (acc, label, index) => {
                                acc[label] =
                                  c.chartData.datasets[0].data[index];
                                return acc;
                              },
                              {},
                            )}
                            totalAnswers={c.count}
                            questionType={c.questionType}
                            colorArray={c.chartData.datasets[0].backgroundColor}
                            maxValue={c.chartData.labels.length - 1}
                          />
                        )}
                        {/* Kommentarer */}
                        {c.comments && c.comments.length > 0 && (
                          <div
                            style={{
                              marginTop: "1.2em",
                              padding: "0.8em 1em",
                              backgroundColor: "#fafafa",
                              border: "1px solid #ddd",
                              borderRadius: "8px",
                            }}
                          >
                            <h5 style={{ marginTop: 0, marginBottom: "0.6em" }}>
                              Kompletterande kommentarer
                            </h5>

                            <div className="textAnswers">
                              {c.comments.map((comment, j) => (
                                <TextAnswer key={j} text={comment} index={j} />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  selectedSurveyId && <p>Inga svar att visa ännu.</p>
                )}
                {selectedSurveyId && responses.length > 0 && (
                  <div
                    style={{
                      marginTop: "3em",
                      borderTop: "1px solid #ccc",
                      paddingTop: "1em",
                    }}
                  >
                    <h3>Se individuella svar</h3>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        paddingLeft: 0,
                      }}
                    >
                      {filteredResp
                        .filter((r) => r.customerId)
                        .sort(
                          (a, b) => b.timestamp?.seconds - a.timestamp?.seconds,
                        ) // nyast först
                        .map((r, index) => (
                          <div
                            key={`${r.customerId}-${index}`}
                            className="customerWrapper"
                          >
                            <div className="customerWrap">
                              <div>
                                <strong>{r.customerId}</strong>
                              </div>
                              <div>
                                {r.timestamp?.toDate
                                  ? r.timestamp
                                      .toDate()
                                      .toLocaleDateString("sv-SE", {
                                        seconds: "never",
                                      })
                                  : "Okänt datum"}
                              </div>
                              <div>{r.employeeId}</div>
                              <div>{r.officeId}</div>
                              <div>
                                {r.createdBy === "public"
                                  ? "webbformulär"
                                  : r.createdBy}
                              </div>
                              <button
                                onClick={() => handleShowAnswer(r.customerId)}
                                className={`customerBtn ${
                                  selectedCustomer === r.customerId
                                    ? "activeCustomerBtn"
                                    : ""
                                }`}
                                style={{ minWidth: 100 }}
                              >
                                Visa
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                <div id="textAnswers">
                  {selectedCustomer && (
                    <div
                      className="fadeSlideOpen"
                      style={{
                        marginTop: "2em",
                        backgroundColor: "#f6f6f6",
                        padding: "1em",
                        borderRadius: "8px",
                      }}
                    >
                      <button
                        className="plainButton"
                        onClick={() => setSelectedCustomer(null)}
                      >
                        <CircleX size={36} strokeWidth={2} color="#333" />
                      </button>
                      <h4>Svar från {selectedCustomer}</h4>
                      {(() => {
                        const response =
                          getResponseByCustomer(selectedCustomer);
                        const selectedForm = forms.find(
                          (f) => f.id === response?.surveyId,
                        );

                        if (!response || !selectedForm)
                          return <p>Inget svar hittades.</p>;

                        const date = response.timestamp?.toDate
                          ? response.timestamp
                              .toDate()
                              .toLocaleDateString("sv-SE", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                          : "okänt datum";

                        const createdBy =
                          response.createdBy || "okänd användare";

                        return (
                          <>
                            <p
                              style={{
                                fontStyle: "italic",
                                marginBottom: "1em",
                              }}
                            >
                              Undersökningen gjord den <strong>{date}</strong>{" "}
                              av{" "}
                              <strong>
                                {createdBy === "public"
                                  ? "inskickat via webbformulär"
                                  : createdBy}
                              </strong>
                              .
                            </p>
                            <ul>
                              {selectedForm.questions.map((q, i) => (
                                <li
                                  key={i}
                                  style={{
                                    marginBottom: "2em",
                                    borderBottom: "1px solid #e0dcdc",
                                    paddingBottom: "0.5em",
                                  }}
                                >
                                  <div
                                    style={{
                                      color: "#444",
                                      fontWeight: "bold",
                                      marginBottom: "0.25em",
                                    }}
                                  >
                                    {q.text}
                                  </div>

                                  <div>
                                    {q.type === "checkbox"
                                      ? (response.responses[i] || "")
                                          .split(";")
                                          .filter(Boolean)
                                          .join(", ") || <em>Inget svar</em>
                                      : response.responses[i] || (
                                          <em>Inget svar</em>
                                        )}
                                  </div>

                                  {response.comments?.[i] &&
                                    response.comments[i].trim() !== "" && (
                                      <div
                                        style={{
                                          marginTop: "0.5em",
                                          padding: "0.6em 0.8em",
                                          backgroundColor: "#fafafa",
                                          borderLeft: "3px solid #ccc",
                                          borderRadius: "4px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: "0.9em",
                                            fontWeight: "bold",
                                            marginBottom: "0.25em",
                                          }}
                                        >
                                          Kompletterande kommentar
                                        </div>
                                        <div>{response.comments[i]}</div>
                                      </div>
                                    )}
                                </li>
                              ))}
                            </ul>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default Visualization;
